import { type IForm } from "@/stores/user-store";
import { Select } from "antd";
import { type FC, useCallback, useEffect, useMemo } from "react";

interface FormSelectProps {
	forms: IForm[];
	selectedFormId: number | undefined;
	isLoading: boolean;
	onFormChange: (formId: number) => void;
	organizationId?: number;
}

const FormSelect: FC<FormSelectProps> = ({
	forms,
	selectedFormId,
	isLoading,
	onFormChange,
	organizationId,
}) => {
	const isOrg28 = organizationId === 28;

	const options = useMemo(
		() =>
			forms
				.filter((form) => form.enabled)
				?.map((form) => {
					// Get template form ID (2, 3, 4, 7, etc.) - the global form template ID
					const templateFormId = form.formId || form.form_id;

					// Determine form name based on template form ID and organization
					let formName: string;
					if (isOrg28) {
						// Org 28: Use specific names for form IDs 2 and 4
						if (templateFormId === 2) {
							formName = "Handyman Checklist";
						} else if (templateFormId === 4) {
							formName = "QC Checklist";
						} else if (templateFormId === 7) {
							formName = "Property Condition Assessment";
						} else {
							formName =
								form?.config?.formName ||
								`Form ${templateFormId || form?.form_id}`;
						}
					} else {
						// Other orgs: Use "Inventory & Tagging" for form ID 3
						if (templateFormId === 3) {
							formName = "Inventory & Tagging";
						} else {
							formName =
								form?.config?.formName ||
								`Form ${templateFormId || form?.form_id}`;
						}
					}

					return {
						value: templateFormId, // Use template form ID (2, 3, 4, 7) not database form_id
						label: formName,
					};
				})
				?.sort((a, b) => (a.value ?? 0) - (b.value ?? 0)),
		[forms, isOrg28],
	);

	const handleChange = useCallback(
		(value: number) => {
			onFormChange(value);
		},
		[onFormChange],
	);

	// If no form is selected and we have forms, select the first one
	useEffect(() => {
		if (!selectedFormId && forms?.length > 0 && !isLoading) {
			let defaultForm: number | undefined;

			if (isOrg28) {
				// For org 28, prefer form ID 2 (Handyman Checklist), then 4 (QC Checklist)
				defaultForm =
					forms.find((form) => (form.formId || form.form_id) === 2)?.formId ||
					forms.find((form) => (form.formId || form.form_id) === 4)?.formId ||
					forms[0]?.formId;
			} else {
				// For other orgs, prefer form ID 3 (Inventory & Tagging)
				defaultForm =
					forms.find((form) => (form.formId || form.form_id) === 3)?.formId ||
					forms.find(
						(form) => form?.config?.formName === "Inventory & Tagging Form",
					)?.formId ||
					forms[0]?.formId;
			}

			if (defaultForm) {
				onFormChange(defaultForm);
			}
		}
	}, [selectedFormId, forms, isLoading, onFormChange, isOrg28]);

	return (
		<Select
			style={{ width: 260 }}
			value={selectedFormId}
			onChange={handleChange}
			options={options}
			placeholder={isLoading ? "Loading forms..." : "Select a form"}
			loading={isLoading}
			disabled={isLoading || forms?.length === 0}
		/>
	);
};

export { FormSelect };
